import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface PointTransaction {
  id: string;
  amount: number;
  type: string;
  reference_id: string | null;
  created_at: string;
}

interface UserPointsData {
  balance: number;
  totalEarned: number;
  transactions: PointTransaction[];
}

async function fetchUserPoints(userId: string): Promise<UserPointsData> {
  const [balanceRes, txRes] = await Promise.all([
    supabase.rpc("get_user_points_balance", { p_user_id: userId }),
    supabase
      .from("point_transactions")
      .select("id, amount, type, reference_id, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(20),
  ]);

  const bal = balanceRes.data
    ? typeof balanceRes.data === "string"
      ? JSON.parse(balanceRes.data)
      : balanceRes.data
    : { balance: 0, total_earned: 0 };

  return {
    balance: bal.balance ?? 0,
    totalEarned: bal.total_earned ?? 0,
    transactions: (txRes.data as PointTransaction[]) ?? [],
  };
}

export function useUserPoints() {
  const { user } = useAuth();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["user-points", user?.id],
    queryFn: () => fetchUserPoints(user!.id),
    enabled: !!user,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  return {
    balance: data?.balance ?? 0,
    totalEarned: data?.totalEarned ?? 0,
    transactions: data?.transactions ?? [],
    loading: isLoading,
    refetch,
  };
}
